import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Link,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import config from '../../config/api';
import { listStylistaTasks, StylistaTask } from '../../api/admin';

const STATUS_OPTIONS = [
  { value: 'all', label: 'Wszystkie' },
  { value: 'queued', label: 'Queued' },
  { value: 'processing', label: 'Processing' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' }
];

const statusColor = (status?: string) => {
  switch (status) {
    case 'queued':
      return 'default';
    case 'processing':
      return 'info';
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
};

const formatTimestamp = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const resolveResultUrl = (value?: string | null) => {
  if (!value) return null;
  if (value.startsWith('http://') || value.startsWith('https://')) {
    return value;
  }
  if (value.startsWith('/')) {
    return `${config.STATIC_URL}${value}`;
  }
  return `${config.STATIC_URL}/${value}`;
};

const StylistaQueue: React.FC = () => {
  const [tasks, setTasks] = useState<StylistaTask[]>([]);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [status, setStatus] = useState<string>('all');
  const [username, setUsername] = useState<string>('');
  const [limit, setLimit] = useState<number>(50);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await listStylistaTasks({
        limit,
        status: status === 'all' ? undefined : status,
        username: username.trim() || undefined
      });
      setTasks(response.tasks || []);
      setPendingCount(response.pending_count || 0);
      setTotal(response.total || 0);
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (err) {
      console.error('Failed to fetch Stylista queue:', err);
      setError(err instanceof Error ? err.message : 'Nie udalo sie pobrac kolejki');
    } finally {
      setLoading(false);
    }
  }, [limit, status, username]);

  useEffect(() => {
    fetchTasks();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = window.setInterval(() => {
      fetchTasks();
    }, 30000);
    return () => window.clearInterval(id);
  }, [autoRefresh, fetchTasks]);

  const columns = useMemo<GridColDef<StylistaTask>[]>(() => ([
    {
      field: 'created_at',
      headerName: 'Utworzono',
      width: 170,
      valueGetter: (params) => {
        const row = params?.row
        if (!row) return null
        return row.created_at || row.started_at || row.updated_at || row.completed_at || null
      },
      renderCell: (params) => {
        const row = params?.row
        const value = row?.created_at || row?.started_at || row?.updated_at || row?.completed_at
        return (
          <Typography variant="caption">
            {formatTimestamp(value)}
          </Typography>
        )
      }
    },
    {
      field: 'username',
      headerName: 'Uzytkownik',
      width: 140
    },
    {
      field: 'model',
      headerName: 'Model',
      width: 140
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value || 'unknown'}
          size="small"
          color={statusColor(params.value as string)}
          variant="outlined"
        />
      )
    },
    {
      field: 'progress',
      headerName: 'Postep',
      width: 110,
      valueGetter: (params) => {
        if (!params) return 0
        const value = params.value;
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
          const parsed = Number(value);
          return Number.isNaN(parsed) ? value : parsed;
        }
        return 0;
      }
    },
    {
      field: 'result_url',
      headerName: 'Wynik',
      width: 240,
      renderCell: (params) => {
        const link = resolveResultUrl(params.value as string);
        if (!link) {
          return <Typography variant="caption" color="text.secondary">-</Typography>;
        }
        return (
          <Link href={link} target="_blank" rel="noreferrer" underline="hover">
            Otworz
          </Link>
        );
      }
    },
    {
      field: 'error',
      headerName: 'Blad',
      width: 260,
      renderCell: (params) => {
        const value = params.value as string | undefined;
        if (!value) {
          return <Typography variant="caption" color="text.secondary">-</Typography>;
        }
        return (
          <Tooltip title={value}>
            <Typography variant="caption" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {value}
            </Typography>
          </Tooltip>
        );
      }
    },
    {
      field: 'task_id',
      headerName: 'Task ID',
      width: 300,
      renderCell: (params) => (
        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
          {params.value}
        </Typography>
      )
    }
  ]), []);

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
            <TextField
              select
              label="Status"
              size="small"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Uzytkownik"
              size="small"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="np. test1"
            />
            <TextField
              label="Limit"
              size="small"
              type="number"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value || 0))}
              inputProps={{ min: 1, max: 200 }}
              sx={{ width: 120 }}
            />
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchTasks}
              disabled={loading}
            >
              Odswiez
            </Button>
            <FormControlLabel
              control={<Switch checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />}
              label="Auto-odswiezanie 30s"
            />
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip label={`Pending: ${pendingCount}`} color="info" variant="outlined" />
            <Chip label={`Razem: ${total}`} color="default" variant="outlined" />
            {lastUpdated && (
              <Typography variant="caption" color="text.secondary">
                Ostatnia aktualizacja: {lastUpdated}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error">{error}</Alert>
      )}

      <Paper variant="outlined" sx={{ p: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            autoHeight
            rows={tasks}
            columns={columns}
            getRowId={(row) => row.task_id}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
          />
        )}
      </Paper>
    </Stack>
  );
};

export default StylistaQueue;
